import React from 'react'
import { Bolt } from 'lucide-react'
import { useSelector } from '@/store'
import { usePanelRegistry } from '@/components/editor/panels/panel-registry'
import { useConfigMode } from '@/components/editor/configuration-mode'
import { findAreaElementForPath } from '@/components/editor/widget-dom'
import { PanelChrome } from '@/components/editor/panels/panel-chrome'
import { SettingsSizeIcons } from '@/components/editor/widget-editor/settings-size-icons'
import { WidgetEditorForm } from '@/components/editor/widget-editor/editor-form'
import {
  hideSettingsOnWidget,
  resizeSettings,
  useSettingsOnWidgetStore,
  WIDGET_EDIT_PANEL,
} from '@/components/editor/widget-editor/settings-size'
import { useWidgetSelectorStore } from './widget-selector'
import { useQueryOverlayStore } from './query-navigation'
import { OverlayFrame } from './query-overlay'

/**
 * The selected widget's settings on top of the widget. Never while the "Edit widgets" panel shows,
 * so only one editor applies changes, and it steps aside while the widget's query is open on it.
 */
export function WidgetSettingsOverlay() {
  const onWidget = useSettingsOnWidgetStore(s => s.onWidget && s.shown)
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)
  const panelShown = usePanelRegistry(r => r.openPanels.has(WIDGET_EDIT_PANEL) || r.floatingPanels.includes(WIDGET_EDIT_PANEL))
  const queryOverlayOpen = useQueryOverlayStore(s => s.path != null && s.size === "overlay")
  const isEditMode = useConfigMode() === "edit"
  // re-resolve the widget's element when the config changes it
  useSelector(s => s.currentViewer.config)

  if (!onWidget || !isEditMode || panelShown || queryOverlayOpen) return null
  const target = findAreaElementForPath(selectedPath)
  if (!target) return null

  return (
    <OverlayFrame id="settings"
                  target={target}
                  label="Widget settings"
                  width={480}
                  height={680}
                  header={(
                    <PanelChrome.Header title="Edit widgets"
                                        icon={Bolt}
                                        onClose={() => {
                                          hideSettingsOnWidget()
                                          useWidgetSelectorStore.setState({ selectedPath: null })
                                        }}
                                        extraIcons={<SettingsSizeIcons current="overlay" />}>
                      Edit widgets
                    </PanelChrome.Header>
                  )}
                  onDetach={placement => resizeSettings("floating", placement)}>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        <WidgetEditorForm />
      </div>
    </OverlayFrame>
  )
}
